import {
  takeLatest,
  call,
  put,
  CallEffect,
  PutEffect,
  ForkEffect,
} from 'redux-saga/effects';
import { PayloadAction, AnyAction } from '@reduxjs/toolkit';

import { buildApiUrl, getApiOrigin } from 'environment';
import createStatusCounts from 'state/shared/createStatusCounts';
import fetchHelper, { isOkHttpResponse } from 'services/apiService';
import type { HttpResponse } from 'services/apiService';
import {
  createErrorId,
  createBackendErrorMessage,
  createNetworkErrorMessage,
} from 'services/errorService';

import type { LoginResponseBody } from '../shared/apiTypes';
import { signInActions } from './signInSlice';
import type { SignInDto } from './signInTypes';

const { SIGN_IN_REQUEST, SIGN_IN_SUCCESS, SIGN_IN_FAILURE } = signInActions;

const statusCounts = createStatusCounts(signInActions);

function* signIn(
  action: PayloadAction<SignInDto>
): Generator<
  CallEffect<AnyAction> | PutEffect<AnyAction>,
  void,
  HttpResponse<LoginResponseBody>
> {
  const {
    payload: { username, password, returnUrl, rememberLogin = false },
  } = action;
  const { type: errorActionType } = SIGN_IN_FAILURE;

  try {
    const response = yield call(fetchHelper.post, buildApiUrl('signIn'), {
      username,
      password,
      returnUrl,
      rememberLogin,
    });
    if (
      isOkHttpResponse<typeof response, LoginResponseBody>(
        response,
        response.parsedBody
      )
    ) {
      const redirectUri = response.parsedBody.redirectUrl
        ? `${getApiOrigin()}${response.parsedBody.redirectUrl}`
        : 'https://bittitan.com';
      yield put(SIGN_IN_SUCCESS());
      window.location.assign(redirectUri);
    } else if (response) {
      // backend error
      yield put(
        SIGN_IN_FAILURE({
          id: createErrorId(
            errorActionType,
            (statusCounts[errorActionType] += 1)
          ),
          error: createBackendErrorMessage(response),
        })
      );
    }
  } catch (error) {
    // network error
    if (error instanceof Error) {
      yield put(
        SIGN_IN_FAILURE({
          id: createErrorId(
            errorActionType,
            (statusCounts[errorActionType] += 1)
          ),
          error: createNetworkErrorMessage(error),
        })
      );
    }
  }
}

export default function* signInSaga(): Generator<
  ForkEffect<never>,
  void,
  unknown
> {
  yield takeLatest(SIGN_IN_REQUEST, signIn);
}
